// src/App.tsx
import React, { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import supabase from "./lib/supabase";
import { clearAllCaches } from "./serviceWorkerRegistration";
import { handleBannedUser } from "./lib/auth";

// Declare NativelyInfo for app state detection
declare global {
  interface Window {
    NativelyInfo?: any;
    natively?: {
      onAppStateChange?: (callback: (state: string) => void) => void;
    };
  }
}

function App() {
  const { setUser, user, updateProfile } = useAuthStore();
  const isSyncingPlayerId = useRef(false);
  const lastSyncTime = useRef<number>(0);
  const syncRetryCount = useRef(0);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('debug_mode')) {
      url.searchParams.delete('debug_mode');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 [AUTH] Starting app initialization...');
      clearAllCaches();
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      console.log('📋 [AUTH] Session check result:', { 
        hasSession: !!session, 
        sessionUser: session?.user?.id, 
        error: error?.message 
      });
      
      if (error) {
        console.error('Session error:', error);
        console.log('❌ [AUTH] Session error - stopping initialization');
        return;
      }
      if (!session?.user) {
        console.log('👤 [AUTH] No session/user found - setting user to null');
        setUser(null);
        return;
      }

      console.log('🔄 [AUTH] Refreshing session...');
      await supabase.auth.refreshSession();

      console.log('🚫 [AUTH] Checking for banned user...');
      const { data: bannedUser, error: banError } = await supabase
        .from("banned_users")
        .select("user_id")
        .eq("user_id", session.user.id)
        .maybeSingle();

      console.log('🚫 [AUTH] Ban check result:', { 
        isBanned: !!bannedUser, 
        banError: banError?.message,
        banErrorCode: banError?.code 
      });

      if (banError && banError.code !== "PGRST116") {
        console.error('Ban check error:', banError);
      }

      if (bannedUser) {
        console.log('🚫 [AUTH] User is banned - handling banned user');
        await handleBannedUser(session.user.id);
        return;
      }

      console.log('👤 [AUTH] Fetching user profile...');
      const { data: profileData, error: profileError } = await supabase.rpc('get_current_user_profile');
      console.log('👤 [AUTH] Profile fetch result:', { 
        hasProfile: !!profileData?.[0], 
        profileId: profileData?.[0]?.id,
        profileError: profileError?.message,
        profileErrorCode: profileError?.code 
      });
      
      if (profileError) {
        if (profileError.code === 'PGRST301' || profileError.message?.includes('JWT')) {
          console.log('🔑 [AUTH] JWT/Auth error - setting user to null');
          setUser(null);
        }
      } else if (profileData?.[0]) {
        console.log('✅ [AUTH] Setting user profile:', profileData[0].id);
        setUser(profileData[0]);
      } else {
        console.log('❌ [AUTH] No profile data received - user remains null');
      }
    };

    console.log('🎯 [AUTH] App initialization triggered');
    initializeApp();

    const interval = setInterval(async () => {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) return;

      const { data: bannedUser } = await supabase
        .from("banned_users")
        .select("user_id")
        .eq("user_id", currentUser.id)
        .maybeSingle();

      if (bannedUser) {
        await handleBannedUser(currentUser.id);
      }
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [setUser]);

  // App state detection for OAuth flow debugging
  useEffect(() => {
    if (!window.NativelyInfo) {
      console.log('📱 [APP_STATE] NativelyInfo not available - skipping app state detection');
      return;
    }

    console.log('📱 [APP_STATE] Setting up app state listener for Universal Link OAuth debugging...');
    
    try {
      const info = new window.NativelyInfo();
      
      const app_state_callback = function (resp: any) {
        console.log('📱 [APP_STATE] App state changed (Universal Link flow):', resp);
        
        if (resp?.state) {
          console.log('📱 [APP_STATE] App went to foreground - Universal Link OAuth may have completed');
          
          // Check if we have a pending auth session
          const authProvider = sessionStorage.getItem('authProvider');
          const authTimestamp = sessionStorage.getItem('authTimestamp');
          
          if (authProvider && authTimestamp) {
            const timeSinceAuth = Date.now() - parseInt(authTimestamp);
            console.log(`📱 [APP_STATE] Universal Link OAuth - provider: ${authProvider}, time since start: ${timeSinceAuth}ms`);
            
            // If OAuth was started recently (within 5 minutes), this might be the return
            if (timeSinceAuth < 300000) {
              console.log('📱 [APP_STATE] Likely returning from Universal Link OAuth flow - browser should dismiss');
            }
          }
        }
      };
      
      info.app_state(app_state_callback);
      console.log('📱 [APP_STATE] ✅ App state listener configured for Universal Link debugging');
      
    } catch (error) {
      console.error('📱 [APP_STATE] ❌ Failed to set up Universal Link app state listener:', error);
    }
  }, []);

  // Enhanced auth state change logging
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 [AUTH_STATE] Auth state changed (Universal Link flow):', { 
        event, 
        hasSession: !!session,
        userId: session?.user?.id,
        provider: session?.user?.app_metadata?.provider,
        timestamp: new Date().toISOString()
      });
      
      if (event === 'SIGNED_IN' && session) {
        const authProvider = sessionStorage.getItem('authProvider');
        const authTimestamp = sessionStorage.getItem('authTimestamp');
        
        if (authProvider && authTimestamp) {
          const timeSinceAuth = Date.now() - parseInt(authTimestamp);
          console.log(`🔐 [AUTH_STATE] ✅ Universal Link OAuth successful! Provider: ${authProvider}, Duration: ${timeSinceAuth}ms`);
          console.log('🔐 [AUTH_STATE] External browser/WebView should now dismiss automatically');
          
          // Clear OAuth tracking
          sessionStorage.removeItem('authProvider');
          sessionStorage.removeItem('authTimestamp');
        } else {
          console.log('🔐 [AUTH_STATE] ✅ Sign in successful (not from tracked OAuth flow)');
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('🔐 [AUTH_STATE] User signed out');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔐 [AUTH_STATE] Token refreshed');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Enhanced OneSignal Player ID sync with retry logic
  const syncPlayerIdAlways = async (forceSync = false) => {
    // Skip if no user or not in mobile app
    if (!user?.id || !window.NativelyNotifications) return;
    
    // Prevent concurrent syncs
    if (isSyncingPlayerId.current) return;
    
    // Rate limiting - don't sync more than once per 30 seconds unless forced
    const now = Date.now();
    if (!forceSync && (now - lastSyncTime.current) < 30000) {
      return;
    }
    
    isSyncingPlayerId.current = true;
    lastSyncTime.current = now;
    
    console.log('🔄 [OneSignal] Checking Player ID...', {
      current: user.onesignal_player_id,
      forceSync
    });
    
    try {
      const notifications = new window.NativelyNotifications();
      
      // First check if we have permission
      notifications.getPermissionStatus((permResp: any) => {
        console.log('📱 [OneSignal] Permission status:', permResp);
        
        if (permResp?.status === true) {
          // We have permission, get the current player ID
          notifications.getOneSignalId((idResp: any) => {
            console.log('🆔 [OneSignal] Player ID response:', idResp);
            
            if (idResp?.playerId && idResp.playerId.trim() !== '') {
              const currentPlayerId = idResp.playerId.trim();
              
              // ALWAYS update if it's different or if we don't have one stored
              if (currentPlayerId !== user.onesignal_player_id || !user.onesignal_player_id) {
                console.log('✅ [OneSignal] Updating Player ID:', {
                  old: user.onesignal_player_id || 'none',
                  new: currentPlayerId
                });
                
                updateProfile({
                  onesignal_player_id: currentPlayerId
                }).then(() => {
                  console.log('✅ [OneSignal] Player ID updated successfully');
                  syncRetryCount.current = 0; // Reset retry count on success
                }).catch((error) => {
                  console.error('❌ [OneSignal] Failed to update Player ID:', error);
                });
              } else {
                console.log('✅ [OneSignal] Player ID is current');
                syncRetryCount.current = 0; // Reset retry count
              }
            } else {
              console.log('⚠️ [OneSignal] No valid Player ID received');
              
              // If we had a player ID before but don't now, clear it
              if (user.onesignal_player_id) {
                console.log('🧹 [OneSignal] Clearing invalid Player ID');
                updateProfile({
                  onesignal_player_id: null
                });
              }
            }
            isSyncingPlayerId.current = false;
          });
        } else {
          console.log('❌ [OneSignal] No notification permission');
          
          // Clear player ID if we don't have permission
          if (user.onesignal_player_id) {
            console.log('🧹 [OneSignal] Clearing Player ID due to no permission');
            updateProfile({
              onesignal_player_id: null
            });
          }
          isSyncingPlayerId.current = false;
        }
      });
    } catch (error) {
      console.error('❌ [OneSignal] Error syncing Player ID:', error);
      isSyncingPlayerId.current = false;
      
      // Retry logic for network errors
      if (syncRetryCount.current < 3) {
        syncRetryCount.current++;
        console.log(`🔄 [OneSignal] Retrying sync (attempt ${syncRetryCount.current}/3)...`);
        setTimeout(() => syncPlayerIdAlways(true), 2000 * syncRetryCount.current);
      }
    }
  };

  // Main sync effect - runs on user change and periodically
  useEffect(() => {
    if (!user?.id || !window.NativelyNotifications) return;
    
    // Run immediately on mount/user change
    syncPlayerIdAlways(true);
    
    // Also run periodically to catch any changes
    const interval = setInterval(() => syncPlayerIdAlways(), 2 * 60 * 1000); // Every 2 minutes
    
    return () => clearInterval(interval);
  }, [user?.id]);

  // Sync when app comes to foreground (focus/resume)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        console.log('👀 [OneSignal] App became visible, syncing Player ID...');
        // Force sync when app comes to foreground
        setTimeout(() => syncPlayerIdAlways(true), 500);
      }
    };
    
    const handleFocus = () => {
      if (user?.id) {
        console.log('🎯 [OneSignal] Window focused, syncing Player ID...');
        setTimeout(() => syncPlayerIdAlways(true), 500);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Also listen for app state changes in BuildNatively
    if (window.natively?.onAppStateChange) {
      window.natively.onAppStateChange((state: string) => {
        if (state === 'active' && user?.id) {
          console.log('📱 [OneSignal] App became active, syncing Player ID...');
          setTimeout(() => syncPlayerIdAlways(true), 500);
        }
      });
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  // Expose manual sync function for debugging
  useEffect(() => {
    // @ts-ignore
    window.syncOneSignalPlayerId = () => {
      console.log('🔧 [OneSignal] Manual sync triggered');
      syncPlayerIdAlways(true);
    };
    
    // @ts-ignore
    window.getOneSignalStatus = () => {
      if (!window.NativelyNotifications) {
        console.log('❌ NativelyNotifications not available');
        return;
      }
      
      const notifications = new window.NativelyNotifications();
      
      notifications.getPermissionStatus((perm: any) => {
        console.log('Permission:', perm);
        
        notifications.getOneSignalId((id: any) => {
          console.log('Player ID:', id);
          console.log('Stored ID:', user?.onesignal_player_id);
        });
      });
    };
  }, [user]);

  return <Outlet />;
}

export default App;

